import { updateVatSettings } from "@/api/settings/take-order-settings";
import { Switch } from "@/components/ui/switch";
import { TabsContent } from "@/components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import useAuth from "@/hooks/useAuth";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

function VatTab(){
    const [vat, setVat] = useState(false);
    const [vatType, setVatType] = useState("inclusive"); // "inclusive" | "exclusive"
    const [showOnRecept, setShowOnRecept] = useState(false);
    const [createYourOwnEnabled, setCreateYourOwnEnabled] = useState(false);
    const [createYourOwnVat, setCreateYourOwnVat] = useState(0);
    const { user } = useAuth();

    // Initialize state on mount from localStorage
    useEffect(() => {
        try {
            const localSettings = localStorage.getItem("settings");
            if (localSettings) {
                const parsed = JSON.parse(localSettings);
                const vatSettings = parsed?.take_order?.vat_settings;
                if (vatSettings) {
                    setVat(Boolean(vatSettings.is_vat_enabled));
                    setVatType(vatSettings.type === "exclusive" ? "exclusive" : "inclusive");
                    setShowOnRecept(Boolean(vatSettings.show_in_receipt));
                    setCreateYourOwnEnabled(Boolean(vatSettings.create_your_own_enabled));
                    setCreateYourOwnVat(Number(vatSettings.create_your_own_vat ?? 0));
                }
            }
        } catch (e) {
            console.error("Failed to parse settings from localStorage", e);
        }
    }, []);

    // Atomic update handler - 1 action = exactly 1 API call
    const handleSave = async (updatedFields = {}) => {
        const newVat = updatedFields.vat !== undefined ? updatedFields.vat : vat;
        const newVatType = updatedFields.vatType !== undefined ? updatedFields.vatType : vatType;
        const newShowOnRecept = updatedFields.showOnRecept !== undefined ? updatedFields.showOnRecept : showOnRecept;
        const newCreateYourOwnEnabled = updatedFields.createYourOwnEnabled !== undefined ? updatedFields.createYourOwnEnabled : createYourOwnEnabled;
        const newCreateYourOwnVat = updatedFields.createYourOwnVat !== undefined ? updatedFields.createYourOwnVat : createYourOwnVat;

        const payload = {
            client_id: user?.client_id,
            is_vat_enabled: newVat,
            type: newVatType,
            show_in_receipt: newVatType === "exclusive" ? true : newShowOnRecept,
            create_your_own_enabled: newCreateYourOwnEnabled,
            create_your_own_vat: Number(newCreateYourOwnVat)
        };

        try {
            const res = await updateVatSettings(user?.restaurant_id, payload);
            if (res.status === "success") {
                const localSettings = localStorage.getItem("settings");
                if (localSettings) {
                    const parsedSettings = JSON.parse(localSettings);
                    parsedSettings.take_order.vat_settings = {
                        ...parsedSettings.take_order?.vat_settings,
                        ...payload
                    };
                    localStorage.setItem("settings", JSON.stringify(parsedSettings));
                }
                toast.success("Vat settings updated successfully");
            } else {
                toast.error(res.message || "Something went wrong");
            }
        } catch (error) {
            console.error("Error saving vat settings:", error);
            toast.error("Something went wrong");
        }
    };

    const handleVatToggle = (checked) => {
        setVat(checked);
        if (checked) {
            handleSave({ vat: true });
        } else {
            setCreateYourOwnEnabled(false);
            setCreateYourOwnVat(0);
            handleSave({ vat: false, createYourOwnEnabled: false, createYourOwnVat: 0 });
        }
    };

    const handleInclusiveToggle = (checked) => {
        if (checked) {
            setVatType("inclusive");
            handleSave({ vatType: "inclusive" });
        } else {
            // When toggling Inclusive off, Exclusive becomes active
            setVatType("exclusive");
            setShowOnRecept(true);
            handleSave({ vatType: "exclusive", showOnRecept: true });
        }
    };

    const handleExclusiveToggle = (checked) => {
        if (checked) {
            setVatType("exclusive");
            setShowOnRecept(true);
            handleSave({ vatType: "exclusive", showOnRecept: true });
        } else {
            // When toggling Exclusive off, Inclusive becomes active
            setVatType("inclusive");
            handleSave({ vatType: "inclusive" });
        }
    };

    const handleShowOnReceiptToggle = (checked) => {
        setShowOnRecept(checked);
        handleSave({ showOnRecept: checked });
    };

    const handleCreateYourOwnToggle = (checked) => {
        setCreateYourOwnEnabled(checked);
        const newRate = checked ? (createYourOwnVat === 0 ? 20 : createYourOwnVat) : 0;
        setCreateYourOwnVat(newRate);
        handleSave({ createYourOwnEnabled: checked, createYourOwnVat: newRate });
    };

    const handleCreateYourOwnVatChange = (val) => {
        const rate = Number(val);
        setCreateYourOwnVat(rate);
        handleSave({ createYourOwnVat: rate });
    };

    return (
        <TabsContent value="vat">
            <div className="w-full p-2">
                {/* Main VAT Switch */}
                <div className="w-full p-2 bg-color2 text-color5 flex items-center justify-between">
                    <span>Vat</span>
                    <Switch checked={vat} onCheckedChange={handleVatToggle} />
                </div>

                {vat && (
                    <>
                        {/* VAT Type Row: Inclusive / Exclusive */}
                        <div className="mt-4 flex gap-2">
                            <div className="w-full">
                                <div className="w-full p-2 bg-color2 text-color5 flex items-center justify-between">
                                    <span>Inclusive Vat</span>
                                    <Switch
                                        checked={vatType === "inclusive"}
                                        onCheckedChange={handleInclusiveToggle}
                                    />
                                </div>
                                {vatType === "inclusive" && (
                                    <div className="w-full p-2 bg-color2 text-color5 flex items-center justify-between">
                                        <span>Show On Receipt</span>
                                        <Switch
                                            checked={showOnRecept}
                                            onCheckedChange={handleShowOnReceiptToggle}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="w-full p-2 bg-color2 text-color5 flex items-center justify-between">
                                <div>
                                    <span>Exclusive Vat</span>
                                    <span className="text-sm font-light text-color6"> ( Will show on Receipt )</span>
                                </div>
                                <Switch
                                    checked={vatType === "exclusive"}
                                    onCheckedChange={handleExclusiveToggle}
                                />
                            </div>
                        </div>

                        {/* Create Your Own VAT Section */}
                        <div className="mt-4 p-2 bg-color2 text-color5 flex flex-col gap-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <span>Create Your Own VAT</span>
                                    <span className="text-sm font-light text-color6 block">Enable VAT for Create Your Own items</span>
                                </div>
                                <Switch
                                    checked={createYourOwnEnabled}
                                    onCheckedChange={handleCreateYourOwnToggle}
                                />
                            </div>

                            {createYourOwnEnabled && (
                                <div className="flex items-center justify-between pt-2 border-t border-color1/20">
                                    <span>Create Your Own VAT Rate</span>
                                    <div className="w-36">
                                        <Select
                                            value={String(createYourOwnVat)}
                                            onValueChange={handleCreateYourOwnVatChange}
                                        >
                                            <SelectTrigger className="w-full bg-cblack3 border-gray-600 text-white">
                                                <SelectValue placeholder="Select VAT" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-cblack3 border-gray-600 text-white">
                                                <SelectItem value="0">0%</SelectItem>
                                                <SelectItem value="20">20%</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </TabsContent>
    );
}

export default VatTab;